import { Component } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';

import { checkCustomerAuthentication } from 'actions/authentication';
import { initializeReportAnErrorCaptcha, reportError, resetReportAnError } from 'actions/productdetail/sharing';
import { trackEvent } from 'helpers/analytics';
import { pick } from 'helpers/lodashReplacement';
import modalStyles from 'styles/components/common/modal.scss';
import css from 'styles/components/productdetail/reportAnError.scss';
const API_FIELDS = ['reply', 'message', 'productId', 'colorId', 'reportedProblems'];
const ERROR_MESSAGE = 'Uh oh, looks like we\'ve encountered an error while sending your message.  This has been logged and we are working on a fix.';
const DONE_MESSAGE = 'Your message has been sent to our content masters for investigation!';
const initialState = { hasSubmittedInvalid: false };

function makeCheckbox(name, label) {
  const id = `reportAnError_${name}`;
  return (
    <div className={css.errorLocation}>
      <input
        type="checkbox"
        name="reportedProblems"
        value={label}
        id={id}
        className={css.checkBox}/>
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

export class ReportAnError extends Component {
  state = initialState;

  componentDidMount() {
    const { checkCustomerAuthentication, colorId, isOpen, initializeReportAnErrorCaptcha, productId } = this.props;
    const returnTo = `/product/${productId}${colorId ? `/color/${colorId}` : ''}#showModal=reportanerror`;
    if (isOpen) {
      checkCustomerAuthentication(returnTo, () => {
        initializeReportAnErrorCaptcha();
      });
    }
  }

  componentDidUpdate({ isOpen: prevOpen }) {
    const { isOpen, initializeReportAnErrorCaptcha } = this.props;
    if (isOpen && !prevOpen) {
      initializeReportAnErrorCaptcha();
    }
  }

  close = e => {
    const { resetReportAnError } = this.props;
    e && e.preventDefault();
    resetReportAnError();
    this.setState(initialState);
  }

  send = e => {
    const { productId, colorId, trackEvent, reportError } = this.props;
    e.preventDefault();
    const { message, reply } = pick(e.currentTarget, API_FIELDS);
    const data = {
      productId,
      colorId,
      message: message && message.value,
      noReply: !reply.checked
    };

    data.reportedProblems = Array.prototype.slice.call(e.currentTarget.reportedProblems)
      .filter(el => el.checked)
      .map(el => el.value)
      .join(', ');

    const captchaAnswer = {
      answer: e.currentTarget.answer.value,
      token: e.currentTarget.token.value
    };
    trackEvent('TE_PDP_REPORTANERROR_SEND', `SKU:${data.productId}`);
    reportError(data, captchaAnswer);
  }

  makeMessageContent(isSuccess) {
    return (
      <div className={css.messageContent}>
        <div className={cn({ [css.complete]: isSuccess, [css.error]: !isSuccess })}>{isSuccess ? DONE_MESSAGE : ERROR_MESSAGE}</div>
        <div className={css.actionBar}>
          <button type="button" onClick={this.close} className={css.doneButton}>Close</button>
        </div>
      </div>
    );
  }

  makeCaptchaContent(captcha) {
    return (
      <div>
        <h3>Are You a Robot?</h3>
        <img
          src={captcha.url}
          height={captcha.height }
          width={captcha.width}
          alt="Captcha" />
        <fieldset>
          <label htmlFor="captchaAnswer" className={css.captchaLabel}>Please type the word you see above</label>
          <input
            id="captchaAnswer"
            type="text"
            name="answer"
            required
            className={cn({ [css.invalid]: captcha.invalid }, css.captchaInput)} />
          <input type="hidden" value={captcha.token} name="token"/>
        </fieldset>
      </div>
    );
  }

  makeModalForm() {
    const { reportAnError: { sending, captcha }, productId, colorId } = this.props;
    const { testId } = this.context;
    return (
      <form method="post" onReset={this.close} onSubmit={this.send}>
        <div className={css.contentContainer}>
          <fieldset>
            <label htmlFor="reportAnErrorDescription" data-test-id={testId('errorDescription')}>What error have you encountered in the product description?</label>
            <textarea
              id="reportAnErrorDescription"
              name="message"
              placeholder="Describe error"
              required={true}
              data-test-id={testId('errorTextBox')}
            />
          </fieldset>
          <h3>Where did the error(s) occur?</h3>
          <div className={css.errorLocationCheckboxes}>
            {makeCheckbox('videoOrImage', 'Video or Images')}
            {makeCheckbox('measurementsOrMaterials', 'Measurements or Materials')}
            {makeCheckbox('sizing', 'Sizing')}
            {makeCheckbox('pricing', 'Pricing')}
            {makeCheckbox('reviews', 'Reviews')}
            {makeCheckbox('inaccurateDescription', 'Inaccurate Description')}
            {makeCheckbox('other', 'Other')}
          </div>
          {this.makeCaptchaContent(captcha)}
          <fieldset>
            <input
              type="checkbox"
              name="reply"
              id="reportAnErrorReply"
              className={css.checkBox}
            />
            <label htmlFor="reportAnErrorReply">I wish to be contacted further about this error.</label>
          </fieldset>
          <input type="hidden" value={productId} name="productId"/>
          <input type="hidden" value={colorId} name="colorId"/>
        </div>
        <div className={css.actionBar}>
          <button type="reset" className={css.closeButton} disabled={sending}>Cancel</button>
          <button type="submit" className={css.sendButton} disabled={sending}>{sending ? 'Sending' : 'Send'}</button>
        </div>
      </form>
    );
  }

  makeModalContent() {
    const { reportAnError: { captcha, successfullySent } } = this.props;
    return (
      <>
        {captcha && captcha.invalid && (<div className={css.error}>The captcha answer was incorrect.</div>)}
        {typeof successfullySent === 'boolean' ? this.makeMessageContent(successfullySent) : this.makeModalForm() }
      </>
    );
  }

  render() {
    const { isOpen } = this.props;
    const { testId } = this.context;
    return (
      <Modal
        isOpen={isOpen}
        overlayClassName={modalStyles.modalOverlay}
        className={cn(modalStyles.modalContent, css.modalContent)}
        contentLabel="Report an Error Popup"
        bodyOpenClassName={css.modalOpenBodyStyle}>
        <div>
          <div className={modalStyles.header}>
            <h2 data-test-id={testId('heading')}>Report An Error</h2>
            <button
              type="button"
              className={modalStyles.close}
              onClick={this.close}
              data-test-id={testId('closeButton')}
              aria-label="Close"/>
          </div>
          {this.makeModalContent()}
        </div>
      </Modal>
    );
  }
}

ReportAnError.propTypes = {
  productId: PropTypes.string.isRequired,
  colorId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired
};

ReportAnError.contextTypes = {
  testId: PropTypes.func
};

ReportAnError.defaultProps = {
  trackEvent
};

function mapStateToProps(state) {
  return { reportAnError: state.sharing.reportAnError };
}
export default connect(mapStateToProps, {
  checkCustomerAuthentication,
  initializeReportAnErrorCaptcha,
  resetReportAnError,
  reportError
})(ReportAnError);
